import React, {
  useState,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
  startTransition,
} from 'react'
import { useQuery } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'
import {
  ErrorMessage,
  Label,
} from '../../../../../ui_reusable/styles/CreateInputUtils.styles'
import {
  ClientPickerHighlight,
  RequiredLabelStar,
  VStack,
} from '../styles/DatasetForm.styles'
import StandardSearchField from '../../../../../ui_reusable/StandardSearchField'
import type { SearchItem } from '../../types/DatasetHandlingTypes'
import type { DatasetCreateRequest } from '../../../../../../api/generated/models'
import { validateAndLog } from '../../../../../../utils/zodDebugger'
import UniversalLoader from '../../../../../ui_reusable/UniversalLoader'
import { useDatasetsMarketsList } from '../../../../../../api/generated/endpoints/datasets/datasets'
import { datasetsMarketsListResponse } from '../../../../../../api/generated/validation/datasets/datasets.zod'

interface DriverScopeSectionProps {
  driverType: 'contextual' | 'client'
  initialMarkets?: SearchItem[]
  initialClient?: SearchItem[]
  isAdmin: boolean
}

export interface DriverScopeSectionRef {
  reset: () => void
}

const fetchClientsV2 = async (): Promise<SearchItem[]> => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/api/v2/clients/?ordering=name&is_active=true`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    },
  )
  if (!response.ok) {
    throw new Error('Failed to load clients')
  }
  const json = await response.json()
  const rows = json.data || json || []
  return rows.map((client: { id?: number; name?: string }) => ({
    id: client.id?.toString() || '0',
    name: client.name || '',
  }))
}

const DriverScopeSection = forwardRef<
  DriverScopeSectionRef,
  DriverScopeSectionProps
>(({ driverType, initialMarkets = [], initialClient = [] }, ref) => {
  const {
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext<DatasetCreateRequest>()

  const needsClients = driverType === 'client'

  const {
    data: clients = [],
    isLoading: clientsLoading,
    error: clientsError,
  } = useQuery({
    queryKey: ['/api/v2/clients/', { ordering: 'name', is_active: true }],
    queryFn: fetchClientsV2,
    enabled: needsClients,
    staleTime: 10 * 60 * 1000,
  })

  const {
    data: marketsResponse,
    isLoading: marketsLoading,
    error: marketsError,
  } = useDatasetsMarketsList(
    { limit: 9999 },
    {
      query: { staleTime: 10 * 60 * 1000 },
    },
  )

  const markets: SearchItem[] = useMemo(() => {
    if (!marketsResponse) return []
    const validatedMarkets = validateAndLog(
      datasetsMarketsListResponse,
      marketsResponse,
      'DriverScope-Markets',
    )
    if (!validatedMarkets || !validatedMarkets.data) return []
    return validatedMarkets.data.map((market) => ({
      id: market.id || 0,
      name: market.name,
      icon: market.icon || undefined,
    }))
  }, [marketsResponse])

  const [selectedMarkets, setSelectedMarkets] =
    useState<SearchItem[]>(initialMarkets)
  const [selectedClientItems, setSelectedClientItems] =
    useState<SearchItem[]>(initialClient)

  useEffect(() => {
    setSelectedMarkets(initialMarkets)
  }, [initialMarkets])

  useEffect(() => {
    setSelectedClientItems(initialClient)
  }, [initialClient])

  useEffect(() => {
    if (driverType !== 'client') {
      setSelectedClientItems([])
    }
  }, [driverType])

  useImperativeHandle(ref, () => ({
    reset: () => {
      setSelectedMarkets([])
      setSelectedClientItems([])
      setValue('market_ids', [])
      setValue('client_id', '')
    },
  }))

  const handleMarketChange = (items: SearchItem[]) => {
    startTransition(() => {
      setSelectedMarkets(items)
      setValue(
        'market_ids',
        items.map((item) => Number(item.id)),
        { shouldValidate: true },
      )
      if (items.length > 0) {
        clearErrors('market_ids')
      }
    })
  }

  const handleClientChange = (items: SearchItem[]) => {
    startTransition(() => {
      setSelectedClientItems(items)
      if (items.length > 0) {
        setValue('client_id', String(items[0].id), { shouldValidate: true })
        clearErrors('client_id')
      } else {
        setValue('client_id', '')
      }
    })
  }

  if (marketsLoading || (needsClients && clientsLoading)) {
    return (
      <UniversalLoader
        show={true}
        content={
          needsClients
            ? 'Loading countries & clients...'
            : 'Loading countries...'
        }
      />
    )
  }

  if (marketsError || (needsClients && clientsError)) {
    return <div>Error loading scope data. Please try again.</div>
  }

  const clientNeedsAttention =
    needsClients && selectedClientItems.length === 0

  return (
    <>
      <VStack style={{ position: 'relative' }}>
        <Label>
          Countries
          <RequiredLabelStar>*</RequiredLabelStar>
        </Label>
        {/* These positioning divs are needed to prevent the dropdown list from pushing down content */}
        <div style={{ position: 'absolute', zIndex: '50' }}>
          <div style={{ height: '1.4rem' }} />
          <StandardSearchField
            data={markets}
            onChange={handleMarketChange}
            placeholder="Select countries..."
            maxSelected={20}
            alwaysShowList={true}
            valueList={selectedMarkets}
            style={{ width: '20rem' }}
            maxHeight={75}
            data-testid="countries-search"
          />
        </div>

        {errors.market_ids && <div style={{ height: '2.4rem' }} />}
        <ErrorMessage style={{ marginTop: '0.2rem' }}>
          {errors.market_ids?.message}
        </ErrorMessage>
      </VStack>

      {/* This div creates dynamic spacing based on the number of selected markets */}
      <div
        style={{
          height: `${selectedMarkets.length > 1 ? 4.5 : 2.4}rem`,
        }}
      />

      {needsClients && (
        <ClientPickerHighlight $active={clientNeedsAttention}>
          <Label>
            Clients
            <RequiredLabelStar>*</RequiredLabelStar>
            {clientNeedsAttention && (
              <span
                style={{
                  marginLeft: '0.4rem',
                  fontWeight: 500,
                  color: '#D96E30',
                  fontSize: '0.85em',
                }}
              >
                Select a client
              </span>
            )}
          </Label>
          <StandardSearchField
            data={clients}
            onChange={handleClientChange}
            placeholder="Search clients..."
            maxSelected={1}
            alwaysShowList={true}
            valueList={selectedClientItems}
            style={{ width: '20rem' }}
            data-testid="clients-search"
          />
          <ErrorMessage>{errors.client_id?.message}</ErrorMessage>
        </ClientPickerHighlight>
      )}
    </>
  )
})

export default DriverScopeSection
